import React, { useEffect, useState } from "react";
import InstallAppDialog, {
  isIOS,
  isStandalone,
  supportsNativeInstall,
} from "@/components/InstallAppDialog";

const DISMISS_KEY = "amc-install-dismissed-at";
const DISMISS_WINDOW_MS = 1000 * 60 * 60 * 24 * 3; // 3 days
const AUTO_DELAY_MS = 4000;

/**
 * Automatically shows the Install dialog a few seconds after the authenticated
 * user lands on the layout, if the PWA is not installed yet and the user
 * hasn't dismissed the prompt recently. On dismissal, we remember the moment
 * so we don't nag the user.
 */
export default function AutoInstallPrompt({ role = "school" }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    try {
      if (localStorage.getItem("amc-pwa-installed") === "1") return;
      const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (dismissedAt && Date.now() - dismissedAt < DISMISS_WINDOW_MS) return;
    } catch {}

    const trigger = () => {
      if (isStandalone()) return;
      setOpen(true);
    };

    // iOS has no beforeinstallprompt — just show the Safari steps.
    if (isIOS()) {
      const t = setTimeout(trigger, AUTO_DELAY_MS);
      return () => clearTimeout(t);
    }

    // Android/Desktop: wait for the native prompt to be available
    if (supportsNativeInstall()) {
      const t = setTimeout(trigger, AUTO_DELAY_MS);
      return () => clearTimeout(t);
    }

    let timeoutId;
    const onAvailable = () => {
      timeoutId = setTimeout(trigger, AUTO_DELAY_MS);
    };
    window.addEventListener("amc:install-available", onAvailable, { once: true });
    return () => {
      window.removeEventListener("amc:install-available", onAvailable);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) {
      try {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      } catch {}
    }
  };

  return (
    <InstallAppDialog open={open} onOpenChange={handleOpenChange} role={role} />
  );
}
